import { createEffect, type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import { gripWhy, useCardMove } from '../../lib/card-move';
import type { MoveSubject } from '../../lib/move-items';
import { restSeconds, useRest } from '../../lib/move-rest';
import { MoveIcon } from './MoveIcon';
import { MoveRing } from './MoveRing';

// The grip on a job row, the single-job layout, an epic row and a capability group header
// (#6771, prototype gripHTML `:1786-1799`): drag it onto another machine's card, or click it
// to open Move to. Shown by its row only when the item can be dragged (`lib/move-items.ts`).
// Disabled, it says why: the machine's own move budget read 0 ("Move limit reached on this
// machine for now"), or the page's flood guard is locked ("Too many changes in a row").
// Touch and pen drag only from here (`components/machine-card/DragLayer.tsx`).
//
// Its row presses it (`pressMove`): the grip itself only stops a disabled press. Focus comes
// back here when Move to closes with Esc, and lands here on the row's new card after a move.
// RESTING (#6772): a job this page moved and that landed rests until its `rest_until`; its grip
// is then the draining ring (`MoveRing`), `aria-disabled`, saying when it can move again.

export function MoveGrip(props: { readonly subject: MoveSubject }): JSX.Element {
  const m = useCardMove();
  let el: HTMLButtonElement | undefined;
  const rest = useRest(() => props.subject);
  const now = (): number => m?.page.clock() ?? 0;
  const why = (): string | null => (m === null ? null : gripWhy(m));
  const label = (): string => {
    const r = rest();
    if (r !== null) {
      return t('fleet.move.ring_label', {
        label: props.subject.label,
        seconds: restSeconds(r, now()),
      });
    }
    const w = why();
    return w === null
      ? t('fleet.move.grip_label', { label: props.subject.label })
      : t('fleet.move.grip_label_why', { label: props.subject.label, why: w });
  };
  const title = (): string => {
    const r = rest();
    if (r !== null) return t('fleet.move.ring_title', { seconds: restSeconds(r, now()) });
    return why() ?? t('fleet.move.grip_title');
  };
  createEffect(() => {
    if (m === null || m.page.focusKey() !== props.subject.key) return;
    if (m.page.takeFocus(props.subject.key)) el?.focus({ preventScroll: true });
  });
  return (
    <button
      ref={el}
      type="button"
      class="move-grip"
      classList={{ 'move-grip--resting': rest() !== null }}
      data-move-grip={props.subject.key}
      aria-expanded={m?.page.menuKey() === props.subject.key}
      aria-disabled={why() === null && rest() === null ? undefined : 'true'}
      aria-label={label()}
      title={title()}
      onClick={() => {
        if (m !== null && why() === null && rest() === null) m.page.openMenu(props.subject);
      }}
    >
      <Show when={rest()} fallback={<MoveIcon glyph="grip" />}>
        {(r) => <MoveRing rest={r()} nowMs={now()} />}
      </Show>
    </button>
  );
}
